import { PropsWithChildren, createContext, useContext, useMemo } from 'react';
import ImportModal from './NxtImport/components/ImportModal';
import { NxtExportProvider, useNxtExport } from './NxtExport/NxtExportContext';
import { NxtImportProvider, useNxtImport } from './NxtImport/NxtImportContext';

interface ExportImportContextType {
  export: ReturnType<typeof useNxtExport>;
  import: ReturnType<typeof useNxtImport>;
}

export const ExportImportContext = createContext<ExportImportContextType | undefined>(
  undefined,
);

export const useExportImport = () => {
  const context = useContext(ExportImportContext);
  if (!context) {
    throw new Error('useExportImport must be used within ExportImportContextProvider');
  }
  return context;
};

export const ExportImportContextProvider = ({ children }: PropsWithChildren) => {
  return (
    <NxtExportProvider>
      <NxtImportProvider>
        <ExportImportContextInner>{children}</ExportImportContextInner>
      </NxtImportProvider>
    </NxtExportProvider>
  );
};

const ExportImportContextInner = ({ children }: PropsWithChildren) => {
  const exportContext = useNxtExport();
  const importContext = useNxtImport();

  const contextValue = useMemo(
    () => ({
      export: exportContext,
      import: importContext,
    }),
    [exportContext, importContext],
  );

  return (
    <ExportImportContext.Provider value={contextValue}>
      <ImportModal />
      {children}
    </ExportImportContext.Provider>
  );
};
